/**
 * Flight route verilerini FlightTicketCard formatına dönüştüren helper fonksiyonlar
 * TicketShowcase component'inde handleFlightCardClick için gerekli tüm alanları ekler
 */

/**
 * URL'den slug'ları çıkarır
 * @param {string} url - Flight route URL'i (örn: "/ucak-bileti/adana-antalya" veya "/ucak-bileti/adana-antalya-ADN-AYT")
 * @returns {{originSlug: string|null, destSlug: string|null}} - Origin ve destination slug'ları
 */
function extractSlugsFromUrl(url) {
  if (!url) {
    return { originSlug: null, destSlug: null };
  }

  const urlParts = url.split("/");
  const routePart = urlParts[urlParts.length - 1]; // "adana-antalya" veya "adana-antalya-ADN-AYT"

  if (!routePart || !routePart.includes("-")) {
    return { originSlug: null, destSlug: null };
  }

  const slugs = routePart.split("-");
  const originSlug = slugs[0] || null;
  // Eğer 4 parça varsa (slug-slug-code-code), 2. parça destination slug
  // Eğer 2 parça varsa, 2. parça destination slug
  const destSlug = slugs.length >= 2 ? slugs[1] : null;

  return { originSlug, destSlug };
}

/**
 * Airport API yanıtını FlightTicketCard formatına dönüştürür
 * @param {Object} route - Airport API'den gelen route objesi
 * @returns {Object} - FlightTicketCard formatında obje
 */
export function mapAirportRouteToFlightCard(route) {
  // URL'den slug'ları çıkar
  const urlToParse = route.UrlForFlightRoute || route.Url;
  const { originSlug, destSlug } = extractSlugsFromUrl(urlToParse);

  return {
    Url: route.Url || route.UrlForFlightRoute,
    OutgoingDepartureDateTime: route.OutgoingDepartureDateTime,
    OutgoingArrivalDateTime: route.OutgoingArrivalDateTime,
    IncomingDepartureDateTime: route.IncomingDepartureDateTime,
    IncomingArrivalDateTime: route.IncomingArrivalDateTime,
    IncomingDepartureDate: route.IncomingDepartureDate,
    FlightDuration: route.FlightDuration,
    Origin: {
      Name: route.Origin?.Name || route.OriginAirportCode,
      LocalizedCityName:
        route.Origin?.LocalizedCityName || route.Origin?.NameWithoutCode,
      // handleFlightCardClick için gerekli alanlar
      Slug: route.Origin?.Slug || originSlug,
      CitySlugName:
        route.Origin?.CitySlugName || route.Origin?.Slug || originSlug,
      Code: route.Origin?.Code || route.OriginAirportCode,
      CityCode:
        route.Origin?.CityCode || route.Origin?.Code || route.OriginAirportCode,
      IsCity: route.Origin?.IsCity ?? true,
    },
    Destination: {
      Name: route.Destination?.Name || route.DestinationAirportCode,
      LocalizedCityName:
        route.Destination?.LocalizedCityName ||
        route.Destination?.NameWithoutCode,
      // handleFlightCardClick için gerekli alanlar
      Slug: route.Destination?.Slug || destSlug,
      CitySlugName:
        route.Destination?.CitySlugName || route.Destination?.Slug || destSlug,
      Code: route.Destination?.Code || route.DestinationAirportCode,
      CityCode:
        route.Destination?.CityCode ||
        route.Destination?.Code ||
        route.DestinationAirportCode,
      IsCity: route.Destination?.IsCity ?? true,
    },
    StopText: route.StopText,
    Price: route.Price,
    Currency: route.Currency,
    AirlineName: route.AirlineName,
    AirlineCode: route.AirlineCode,
  };
}

/**
 * Airline API yanıtını FlightTicketCard formatına dönüştürür
 * @param {Object} route - Airline API'den gelen route objesi
 * @returns {Object} - FlightTicketCard formatında obje
 */
export function mapAirlineRouteToFlightCard(route) {
  // URL'den slug'ları çıkar
  const { originSlug, destSlug } = extractSlugsFromUrl(route.Url);

  // Origin bilgileri - API response'undan gelen veriyi kullan
  const originName = `${route.OriginCity || ""} (${route.OriginCityCode || route.Origin || ""})`;
  const originLocalizedCityName = route.OriginCity || "";
  const originCode = route.OriginCityCode || route.Origin || "";
  const originIsCity = true; // Havayolu rotaları genellikle şehir bazlıdır

  // Destination bilgileri - API response'undan gelen veriyi kullan
  const destName = `${route.DestinationCity || ""} (${route.DestinationCityCode || route.Destination || ""})`;
  const destLocalizedCityName = route.DestinationCity || "";
  const destCode = route.DestinationCityCode || route.Destination || "";
  const destIsCity = true; // Havayolu rotaları genellikle şehir bazlıdır

  return {
    Url: route.Url,
    OutgoingDepartureDateTime: route.OutgoingDepartureDate,
    OutgoingArrivalDateTime: route.OutgoingArrivalDate,
    FlightDuration: `${Math.floor(route.DurationInMinutes / 60)}s ${route.DurationInMinutes % 60}dk`,
    Origin: {
      Name: originName,
      LocalizedCityName: originLocalizedCityName,
      Code: originCode,
      CityCode: originCode,
      Slug: originSlug,
      CitySlugName: originSlug,
      IsCity: originIsCity,
    },
    Destination: {
      Name: destName,
      LocalizedCityName: destLocalizedCityName,
      Code: destCode,
      CityCode: destCode,
      Slug: destSlug,
      CitySlugName: destSlug,
      IsCity: destIsCity,
    },
    StopText: route.NumStops === 0 ? "Direkt" : `${route.NumStops} Aktarma`,
    Price: route.AdultTotalFare,
    Currency: route.Currency,
    IsRefundable: route.IsRefundable,
    MinAvailableSeats: route.MinAvailableSeats,
    Airline: route.Airline,
  };
}

/**
 * Generic route mapper - API yanıt formatını otomatik algılar
 * @param {Object} route - Herhangi bir API'den gelen route objesi
 * @returns {Object} - FlightTicketCard formatında obje
 */
export function mapRouteToFlightCard(route) {
  // Airport API formatını kontrol et
  if (
    route.OriginAirportCode ||
    route.DestinationAirportCode ||
    route.UrlForFlightRoute
  ) {
    return mapAirportRouteToFlightCard(route);
  }

  // Airline API formatını kontrol et
  if (
    route.OriginCity ||
    route.DestinationCity ||
    route.OriginCityCode ||
    route.DestinationCityCode
  ) {
    return mapAirlineRouteToFlightCard(route);
  }

  // Fallback: Mevcut yapıyı koru, sadece eksik alanları ekle
  const urlToParse = route.UrlForFlightRoute || route.Url;
  const { originSlug, destSlug } = extractSlugsFromUrl(urlToParse);

  return {
    ...route,
    Origin: {
      ...route.Origin,
      Slug: route.Origin?.Slug || originSlug,
      CitySlugName:
        route.Origin?.CitySlugName || route.Origin?.Slug || originSlug,
      Code: route.Origin?.Code || route.Origin?.CityCode,
      CityCode: route.Origin?.CityCode || route.Origin?.Code,
      IsCity: route.Origin?.IsCity ?? true,
    },
    Destination: {
      ...route.Destination,
      Slug: route.Destination?.Slug || destSlug,
      CitySlugName:
        route.Destination?.CitySlugName || route.Destination?.Slug || destSlug,
      Code: route.Destination?.Code || route.Destination?.CityCode,
      CityCode: route.Destination?.CityCode || route.Destination?.Code,
      IsCity: route.Destination?.IsCity ?? true,
    },
  };
}
